<template>
    <div
        class="card relative flex w-full justify-between px-4 py-2 text-sm"
        data-test="file-row"
    >
        <!--
            The whole bar opens the file. A button stretched over the row rather
            than a click handler on the row itself: it keeps the real button
            semantics (focus, Enter/Space, a name for screen readers) while
            leaving the menu beside it clickable, which a button wrapping the
            whole row could not - a button inside a button is invalid markup.
        -->
        <button
            type="button"
            class="rounded-muval-2 absolute inset-0"
            :aria-label="`Open ${label}`"
            @click="emit('open')"
        ></button>

        <div class="pointer-events-none relative my-auto flex min-w-0 items-center gap-4">
            <div class="relative shrink-0">
                <SvgIcon
                    name="system-document"
                    class="text-muval-gray-3 size-8"
                />
                <span
                    v-if="extension"
                    class="absolute bottom-2 left-px rounded-xs px-1 text-[8px] leading-tight font-medium text-white uppercase"
                    :class="getExtensionBgClass(extension)"
                >
                    {{ extension }}
                </span>
            </div>
            <div class="flex min-w-0 flex-col items-start gap-y-1">
                <div class="flex min-w-0 flex-col items-start md:flex-row md:items-center md:gap-x-4">
                    <span class="max-w-40 overflow-hidden text-left text-ellipsis whitespace-nowrap md:max-w-full">
                        {{ label }}
                    </span>
                    <span
                        v-if="file.created_at"
                        class="text-muval-gray-3 shrink-0 text-sm"
                        >{{ uploadedAt }}</span
                    >
                </div>
                <FileChips :file="file">
                    <div
                        v-if="failure"
                        class="card text-muval-gray-1 flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs"
                        data-test="file-row-preview-failure"
                    >
                        <SvgIcon
                            name="system-warning"
                            class="text-muval-red-1 size-3"
                        />
                        <span>{{ failure.label }}</span>
                    </div>
                </FileChips>
            </div>
        </div>

        <div class="text-muval-gray-1 relative flex items-center text-xs">
            <FileActionsMenu
                :can-edit="canEdit"
                :can-delete="canDelete"
                @download="emit('download')"
                @edit="emit('edit')"
                @delete="emit('delete')"
            >
                <template
                    v-if="$slots['menu-items']"
                    #menu-items="{ run }"
                >
                    <slot
                        name="menu-items"
                        :run="run"
                    />
                </template>
            </FileActionsMenu>
        </div>
    </div>
</template>

<script setup lang="ts">
import FileActionsMenu from '@components/files/FileActionsMenu.vue';
import FileChips from '@components/files/FileChips.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import useDayJs from '@plugins/useDayJs';
import { useUserStore } from '@store/user';
import { getExtensionBgClass, getFileDisplayName, getFileExtension, previewFailure } from '@utilities/fileDisplay';

/**
 * One stored file, as every file list in the CRM presents it: a document icon
 * carrying the file's extension, the label it is filed under, when it was
 * uploaded, its sharing and tag chips (and why it can't be previewed), and a menu.
 */

const props = withDefaults(
    defineProps<{
        file: Record<string, any>;
        /** Overrides the label where a collection has a name of its own. */
        label?: string;
        canEdit?: boolean;
        canDelete?: boolean;
    }>(),
    {
        label: undefined,
        canEdit: false,
        canDelete: false,
    },
);

const emit = defineEmits<{ open: []; download: []; edit: []; delete: [] }>();

const dayjs = useDayJs();
const userStore = useUserStore();

const label = computed(() => props.label || getFileDisplayName(props.file));
const extension = computed(() => getFileExtension(props.file));
const failure = computed(() => previewFailure(props.file));

const uploadedAt = computed(() => {
    const timezone = userStore.getTimezone;
    const at = dayjs(props.file.created_at);

    return (timezone ? at.tz(timezone) : at).format('DD/MM/YY h:mmA');
});
</script>
